<template>
  <TransitionRoot appear as="template" :show="isVisible">
    <Dialog as="div" class="relative z-[10000]" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4" @click.stop>
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-gray-800">
                <div class="flex items-center justify-between p-6 border-b border-gray-600">
                  <h3 class="text-lg font-semibold leading-6 text-white">
                    {{ isEditMode ? 'Edit Service' : 'Add Service' }}
                  </h3>
                  <button type="button" @click="closeDialog" :disabled="isLoading"
                    class="rounded-md text-gray-400 hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <span class="sr-only">Close</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6">
                  <div class="space-y-6">
                    <!-- Images -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Images</label>
                      <div class="grid grid-cols-3 gap-4">
                        <div v-for="(preview, index) in previewImages" :key="index"
                          class="relative h-[100px] w-full rounded-lg bg-gray-700 border-2 border-gray-600 overflow-hidden">
                          <img :src="preview" alt="Image Preview" class="h-full w-full object-cover" />
                          <button @click="removeImage(index)"
                            class="absolute top-1 right-1 bg-red-600 text-white rounded-full h-6 w-6 flex items-center justify-center hover:bg-red-700">
                            <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div
                          class="h-[100px] w-full rounded-lg bg-gray-700 border-2 border-gray-600 flex items-center justify-center cursor-pointer"
                          @click="$refs.fileInput.click()" :class="{ 'border-red-400': errors.images }">
                          <span class="text-gray-400 text-sm font-medium">Add Image</span>
                        </div>
                      </div>
                      <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/jpg" multiple
                        @change="handleImageChange" class="hidden" />
                      <p v-if="errors.images" class="mt-1 text-sm text-red-400">{{ errors.images }}</p>
                      <p v-if="imageUploading" class="mt-1 text-sm text-blue-400">Uploading images...</p>
                    </div>

                    <!-- Title -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Title *</label>
                      <input v-model="formData.title" type="text" maxlength="100"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.title }" />
                      <p v-if="errors.title" class="mt-1 text-sm text-red-400">{{ errors.title }}</p>
                    </div>

                    <!-- Description -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Description</label>
                      <textarea v-model="formData.description" rows="4" maxlength="500"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.description }" placeholder="Enter service description" />
                      <p v-if="errors.description" class="mt-1 text-sm text-red-400">{{ errors.description }}</p>
                    </div>






                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Basic Price *</label>
                      <input v-model="formData.basicPrice" type="text"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.basicPrice }" placeholder="Enter Basic price in USD" />
                      <p v-if="errors.basicPrice" class="mt-1 text-sm text-red-400">{{ errors.basicPrice }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Basic Info</label>
                      <div class="space-y-2">
                        <div v-for="(item, index) in formData.basicInfo" :key="index" class="flex items-center gap-2">
                          <input v-model="formData.basicInfo[index]" type="text"
                            class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter info item" />
                          <button @click="removeItem('basicInfo', index)" type="button"
                            class="text-red-400 hover:text-red-300">Remove</button>
                        </div>
                        <button @click="addItem('basicInfo')" type="button"
                          class="mt-2 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700">Add Item</button>
                      </div>
                      <p v-if="errors.basicInfo" class="mt-1 text-sm text-red-400">{{ errors.basicInfo }}</p>
                    </div>

                    <!-- Standard Price and Info -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Standard Price *</label>
                      <input v-model="formData.standardPrice" type="text"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.standardPrice }" placeholder="Enter Standard price in USD" />
                      <p v-if="errors.standardPrice" class="mt-1 text-sm text-red-400">{{ errors.standardPrice }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Standard Info</label>
                      <div class="space-y-2">
                        <div v-for="(item, index) in formData.standardInfo" :key="index"
                          class="flex items-center gap-2">
                          <input v-model="formData.standardInfo[index]" type="text"
                            class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter info item" />
                          <button @click="removeItem('standardInfo', index)" type="button"
                            class="text-red-400 hover:text-red-300">Remove</button>
                        </div>
                        <button @click="addItem('standardInfo')" type="button"
                          class="mt-2 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700">Add Item</button>
                      </div>
                      <p v-if="errors.standardInfo" class="mt-1 text-sm text-red-400">{{ errors.standardInfo }}</p>
                    </div>

                    <!-- Premium Price and Info -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Premium Price *</label>
                      <input v-model="formData.premiumPrice" type="text"
                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        :class="{ 'border-red-400': errors.premiumPrice }" placeholder="Enter Premium price in USD" />
                      <p v-if="errors.premiumPrice" class="mt-1 text-sm text-red-400">{{ errors.premiumPrice }}</p>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Premium Info</label>
                      <div class="space-y-2">
                        <div v-for="(item, index) in formData.premiumInfo" :key="index" class="flex items-center gap-2">
                          <input v-model="formData.premiumInfo[index]" type="text"
                            class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter info item" />
                          <button @click="removeItem('premiumInfo', index)" type="button"
                            class="text-red-400 hover:text-red-300">Remove</button>
                        </div>
                        <button @click="addItem('premiumInfo')" type="button"
                          class="mt-2 px-4 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700">Add Item</button>
                      </div>
                      <p v-if="errors.premiumInfo" class="mt-1 text-sm text-red-400">{{ errors.premiumInfo }}</p>
                    </div>
                    <!-- Status Checkbox -->
                    <div>
                      <label class="block text-sm font-medium text-gray-200 mb-1">Status</label>
                      <input v-model="formData.status" type="checkbox"
                        class="h-4 w-4 text-blue-600 border-gray-600 rounded focus:ring-blue-500 bg-gray-700" />
                      <span class="ml-2 text-gray-300">Active</span>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                  <button @click="closeDialog" type="button" :disabled="isLoading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 border border-gray-500 rounded-md shadow-sm hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                  <button @click="handleSubmit" type="button" :disabled="isLoading || imageUploading"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    <template v-if="isLoading">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </template>
                    <template v-else>{{ isEditMode ? 'Update' : 'Create' }}</template>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { projectFirestore, projectStorage, projectAuth } from '@/firebase/config';
import { doc, setDoc, getDoc, serverTimestamp, collection } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  serviceId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['onClose', 'onEmitHandleEvent']);

const isLoading = ref(false);
const imageUploading = ref(false);
const formData = ref({
  title: '',
  description: '',
  imageURLs: [],
  basicPrice: '',
  basicInfo: [],
  standardPrice: '',
  standardInfo: [],
  premiumPrice: '',
  premiumInfo: [],
  status: true,
  createdAt: null, 
  createdBy: '',  
  updatedAt: null, 
  updatedBy: '',
});
const errors = ref({});
const imageFiles = ref([]);
const previewImages = ref([]);
const isEditMode = computed(() => !!props.serviceId);

const closeDialog = () => {
  formData.value = {
    title: '',
    description: '',
    imageURLs: [],
    basicPrice: '',
    basicInfo: [],
    standardPrice: '',
    standardInfo: [],
    premiumPrice: '',
    premiumInfo: [],
    status: true,
    createdAt: null, 
    createdBy: '',  
    updatedAt: null, 
    updatedBy: '', 
  };
  errors.value = {};
  imageFiles.value = [];
  previewImages.value = [];
  emit('onEmitHandleEvent', 'close');
};

const handleImageChange = (event) => {
  const files = Array.from(event.target.files);
  if (!files.length) return;

  files.forEach(file => {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      errors.value.images = 'Please upload only JPG or PNG images.';
      return;
    }
    errors.value.images = null;
    imageFiles.value.push(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      previewImages.value.push(e.target.result);
    };
    reader.readAsDataURL(file);
  });
};

const removeImage = (index) => {
  previewImages.value.splice(index, 1);
  imageFiles.value.splice(index, 1);
  if (isEditMode.value && formData.value.imageURLs[index]) {
    formData.value.imageURLs.splice(index, 1);
  }
  errors.value.images = null;
};

const addItem = (field) => {
  formData.value[field].push('');
};

const removeItem = (field, index) => {
  formData.value[field].splice(index, 1);
};

const validateForm = () => {
  errors.value = {};
  let isValid = true;

  if (!formData.value.title.trim()) {
    errors.value.title = 'Title is required';
    isValid = false;
  }
  if (formData.value.description && formData.value.description.length > 500) {
    errors.value.description = 'Description cannot exceed 500 characters';
    isValid = false;
  }

  if (formData.value.imageURLs.length === 0 && imageFiles.value.length === 0) {
    errors.value.images = 'At least one image is required';
    isValid = false;
  }

  return isValid;
};


const handleSubmit = async () => {
  if (isLoading.value || imageUploading.value) return;

  if (!validateForm()) return;

  isLoading.value = true;

  try {
    let imageURLs = [...formData.value.imageURLs];

    if (imageFiles.value.length > 0) {
      imageUploading.value = true;
      const uniqueId = props.serviceId || Date.now().toString();

      // Delete old images if in edit mode and new images are uploaded
      if (isEditMode.value && formData.value.imageURLs.length > 0) {
        imageURLs = [];
      }

      // Upload new images
      for (let i = 0; i < imageFiles.value.length; i++) {
        const file = imageFiles.value[i];
        const imagePath = `services/${uniqueId}_${i}_${file.name}`;
        const imageRef = storageRef(projectStorage, imagePath);
        const snapshot = await uploadBytes(imageRef, file);
        const url = await getDownloadURL(snapshot.ref);
        imageURLs.push(url);
      }
    }

    const currentUser = projectAuth.currentUser;
    if (!currentUser) throw new Error('No authenticated user found.');

    const serviceData = {
      title: formData.value.title.trim(),
      description: formData.value.description.trim() || '',
      imageURLs: imageURLs,
      basicPrice: formData.value.basicPrice,
      basicInfo: formData.value.basicInfo.map(item => item.trim()).filter(item => item),
      standardPrice: formData.value.standardPrice,
      standardInfo: formData.value.standardInfo.map(item => item.trim()).filter(item => item),
      premiumPrice: formData.value.premiumPrice,
      premiumInfo: formData.value.premiumInfo.map(item => item.trim()).filter(item => item),
      status: formData.value.status,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.uid,
    };

    if (!isEditMode.value) {
      serviceData.createdAt = serverTimestamp();
      serviceData.createdBy = currentUser.uid;
    }

    const serviceRef = doc(projectFirestore, 'services', props.serviceId || doc(collection(projectFirestore, 'services')).id);
    await setDoc(serviceRef, serviceData, { merge: isEditMode.value });

    emit('onEmitHandleEvent', isEditMode.value ? 'updated' : 'created');
  } catch (error) {
    console.error('Error saving service:', error);
    const errorMessage = error.message || 'Failed to save service. Please try again.';
    emit('onEmitHandleEvent', 'error', true, errorMessage);
  } finally {
    isLoading.value = false;
    imageUploading.value = false;
  }
};

const fetchService = async () => {
  if (!props.serviceId) return;
  try {
    const serviceRef = doc(projectFirestore, 'services', props.serviceId);
    const serviceDoc = await getDoc(serviceRef);
    if (serviceDoc.exists()) {
      const data = serviceDoc.data();
      formData.value = {
        title: data.title || '',
        description: data.description || '',
        demoLink: data.demoLink || '',
        imageURLs: Array.isArray(data.imageURLs) ? data.imageURLs : [],
        basicPrice: data.basicPrice || '',
        basicInfo: Array.isArray(data.basicInfo) ? data.basicInfo : [],
        standardPrice: data.standardPrice || '',
        standardInfo: Array.isArray(data.standardInfo) ? data.standardInfo : [],
        premiumPrice: data.premiumPrice || '',
        premiumInfo: Array.isArray(data.premiumInfo) ? data.premiumInfo : [],
        status: data.status || true,
      };
      previewImages.value = [...(formData.value.imageURLs || [])];
    } else {
      emit('onEmitHandleEvent', 'error', true, 'Service not found.');
    }
  } catch (error) {
    console.error('Error fetching service:', error);
    emit('onEmitHandleEvent', 'error', true, 'Failed to load service data.');
  }
};

watch(
  () => props.serviceId,
  (newServiceId) => {
    if (newServiceId) {
      fetchService();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
input,
select,
textarea {
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

input:focus,
select:focus,
textarea:focus {
  border-color: #3b82f6;
}

button:disabled {
  cursor: not-allowed;
}
</style>